<template>
  <v-container>
    <v-row v-if="noData">
      <v-col cols="12" md="12" align="center">
        No hay datos disponibles para las tendencias por rango horario
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12" md="8">
        <v-skeleton-loader type="image" v-if="loading"></v-skeleton-loader>

        <v-card outlined color="transparent" v-else>
          <v-card-subtitle>
            Tendencias por rango horario
          </v-card-subtitle>
          <v-card-text>
            <div ref="chartTrends" style="width: 100%; height: 400px;"></div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-skeleton-loader type="image" v-if="loading"></v-skeleton-loader>

        <v-card outlined color="transparent" v-else>
          <v-card-subtitle>
            Tendencias del rango seleccionado
          </v-card-subtitle>
          <v-card-text>
            <div ref="chartSummary" style="width: 100%; height: 400px;"></div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'DeviceStatsTrendChart',
  props: {
    deviceId: {
      type: [String, Number],
      required: true
    },
    sensorName: {
      type: String,
      required: true
    },
  },
  data() {
    return {
      loading: false,
      noData: false,
      chartTrendsInstance: null,
      chartSummaryInstance: null
    }
  },
  computed: {
    deviceStatsTrend() {
      return this.$store.getters.deviceStatsTrend
    },
    viewDate () {
      return this.$store.getters.viewDate
    },
  },
  watch: {
    deviceId() {
      this.fetchData()
    },
    sensorName() {
      this.fetchData()
    },
  },
  mounted() {
    this.fetchData()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    if (this.chartTrendsInstance) {
      this.chartTrendsInstance.dispose()
    }
    if (this.chartSummaryInstance) {
      this.chartSummaryInstance.dispose()
    }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() {
      if (this.chartTrendsInstance) {
        this.chartTrendsInstance.resize()
      }
      if (this.chartSummaryInstance) {
        this.chartSummaryInstance.resize()
      }
    },

    async fetchData() {
      this.loading = true

      try {
        let payload = {
          deviceId: this.deviceId,
          sensorName: this.sensorName,
        }

        if (Array.isArray(this.viewDate)) {
            const range = this.sortRange(this.viewDate)
            payload.startDate = range.from
            payload.endDate = range.to
        } else {
            payload.startDate = this.viewDate
            payload.endDate = this.viewDate
        }

        await this.$store.dispatch('pullDeviceStatsTrend', payload)

        // Primero cambiamos loading a false para que los refs se monten en el DOM
        this.loading = false

        // Luego procesamos los gráficos cuando el DOM esté listo
        this.$nextTick(() => {
          this.processChartData(this.deviceStatsTrend)
        })
      } catch (error) {
        console.error('Error fetching device stats trend:', error)
        this.loading = false
      }
    },

    processChartData(data) {
      this.noData = this.checkData()

      if (this.noData) {
        return
      }

      this.$nextTick(() => {
        this.processTrendChart(data.message.dayStatsRange)
        this.processSummaryChart(data.message.dailyStatsRangeProcess)
      })
    },

    parseMode(mode) {
      if (mode === undefined || mode === null) {
        return { modes: [] }
      }

      if (typeof mode === 'string' && mode.includes(',')) {
        const parts = mode.split(',')
          .map(part => Number(part.trim()))
          .filter(part => Number.isFinite(part))
          .map(part => Math.round(part))
          .sort((a, b) => a - b)
          .slice(0, 3)
        return { modes: parts }
      }

      if (typeof mode === 'number') {
        return { modes: [Math.round(mode)] }
      }

      if (Array.isArray(mode)) {
        const parts = mode
          .map(part => Number(part))
          .filter(part => Number.isFinite(part))
          .map(part => Math.round(part))
          .sort((a, b) => a - b)
          .slice(0, 3)
        return { modes: parts }
      }

      const numericMode = Number(mode)
      return { modes: Number.isFinite(numericMode) ? [Math.round(numericMode)] : [] }
    },

    processTrendChart(dayStatsRange) {
      if (!this.$refs.chartTrends) {
        return
      }

      // Inicializar o reutilizar instancia de ECharts
      if (!this.chartTrendsInstance) {
        this.chartTrendsInstance = echarts.init(this.$refs.chartTrends)
      }

      // Construir datos para 9 series (3 rangos x hasta 3 modas cada uno)
      const madrugadaBase = []
      const madrugadaSecond = []
      const madrugadaThird = []
      const diurnoBase = []
      const diurnoSecond = []
      const diurnoThird = []
      const nocturnoBase = []
      const nocturnoSecond = []
      const nocturnoThird = []
      const dateLabels = []

      dayStatsRange.forEach(day => {
        const formattedDate = this.formatDate(day.fecha)
        dateLabels.push(formattedDate)

        const madrugada = day.grupos.find(g => g.grupo === 'Madrugada')
        const diurno = day.grupos.find(g => g.grupo === 'Diurno')
        const nocturno = day.grupos.find(g => g.grupo === 'Nocturno')

        const m = this.parseMode(madrugada && madrugada.mode).modes
        const d = this.parseMode(diurno && diurno.mode).modes
        const n = this.parseMode(nocturno && nocturno.mode).modes

        // Madrugada
        madrugadaBase.push(m[0] != null ? m[0] : null)
        madrugadaSecond.push(m[1] != null && m[0] != null ? Math.abs(m[1] - m[0]) : null)
        madrugadaThird.push(m[2] != null && m[1] != null ? Math.abs(m[2] - m[1]) : null)

        // Diurno
        diurnoBase.push(d[0] != null ? d[0] : null)
        diurnoSecond.push(d[1] != null && d[0] != null ? Math.abs(d[1] - d[0]) : null)
        diurnoThird.push(d[2] != null && d[1] != null ? Math.abs(d[2] - d[1]) : null)

        // Nocturno
        nocturnoBase.push(n[0] != null ? n[0] : null)
        nocturnoSecond.push(n[1] != null && n[0] != null ? Math.abs(n[1] - n[0]) : null)
        nocturnoThird.push(n[2] != null && n[1] != null ? Math.abs(n[2] - n[1]) : null)
      })

      // Calcular rango Y dinámico
      const allNumbers = [
        ...madrugadaBase, ...diurnoBase, ...nocturnoBase
      ].filter(v => v !== null)

      madrugadaBase.forEach((base, i) => {
        if (base !== null && madrugadaSecond[i] !== null) allNumbers.push(base + madrugadaSecond[i])
        if (base !== null && madrugadaSecond[i] !== null && madrugadaThird[i] !== null) {
          allNumbers.push(base + madrugadaSecond[i] + madrugadaThird[i])
        }
      })
      diurnoBase.forEach((base, i) => {
        if (base !== null && diurnoSecond[i] !== null) allNumbers.push(base + diurnoSecond[i])
        if (base !== null && diurnoSecond[i] !== null && diurnoThird[i] !== null) {
          allNumbers.push(base + diurnoSecond[i] + diurnoThird[i])
        }
      })
      nocturnoBase.forEach((base, i) => {
        if (base !== null && nocturnoSecond[i] !== null) allNumbers.push(base + nocturnoSecond[i])
        if (base !== null && nocturnoSecond[i] !== null && nocturnoThird[i] !== null) {
          allNumbers.push(base + nocturnoSecond[i] + nocturnoThird[i])
        }
      })

      const minY = allNumbers.length > 0 ? Math.min(...allNumbers) - 5 : 40

      // Mostrar labels solo si hay pocos días (menos de 8)
      const showLabels = dayStatsRange.length < 8

      // Calcular intervalo para fechas en eje X
      let dateInterval = 0
      if (dayStatsRange.length > 15) {
        dateInterval = Math.floor(dayStatsRange.length / 10)
      }

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const dateIndex = params[0].dataIndex
            let result = dateLabels[dateIndex] + '<br/>'

            params.forEach(p => {
              // Saltar series auxiliares y valores nulos/undefined
              if (p.seriesName.includes('-second') || p.seriesName.includes('-third')) return
              if (p.value === null || p.value === undefined) return

              const baseVal = p.value
              const values = [baseVal]

              const secondSeries = params.find(s => s.seriesName === p.seriesName + '-second')
              if (secondSeries && secondSeries.value !== null && secondSeries.value !== undefined && secondSeries.value > 0) {
                values.push(baseVal + secondSeries.value)
              }

              const thirdSeries = params.find(s => s.seriesName === p.seriesName + '-third')
              if (thirdSeries && thirdSeries.value !== null && thirdSeries.value !== undefined && thirdSeries.value > 0) {
                values.push(baseVal + (secondSeries && secondSeries.value ? secondSeries.value : 0) + thirdSeries.value)
              }

              const line = p.seriesName + ': <strong>' + values.join(', ') + ' dBA</strong>'
              result += line + '<br/>'
            })
            return result
          }
        },
        legend: {
          data: [
            {
              name: 'Madrugada',
              icon: 'rect',
              itemStyle: {
                color: {
                  type: 'linear',
                  x: 0, y: 0, x2: 0, y2: 1,
                  colorStops: [
                    { offset: 0, color: 'rgba(99, 142, 163, 0.85)' },
                    { offset: 0.5, color: 'rgba(99, 142, 163, 0.85)' },
                    { offset: 0.5, color: '#ADD8E6' },
                    { offset: 1, color: '#ADD8E6' }
                  ]
                }
              }
            },
            {
              name: 'Diurno',
              icon: 'rect',
              itemStyle: {
                color: {
                  type: 'linear',
                  x: 0, y: 0, x2: 0, y2: 1,
                  colorStops: [
                    { offset: 0, color: 'rgba(204, 132, 0, 0.85)' },
                    { offset: 0.5, color: 'rgba(204, 132, 0, 0.85)' },
                    { offset: 0.5, color: '#FFA500' },
                    { offset: 1, color: '#FFA500' }
                  ]
                }
              }
            },
            {
              name: 'Nocturno',
              icon: 'rect',
              itemStyle: {
                color: {
                  type: 'linear',
                  x: 0, y: 0, x2: 0, y2: 1,
                  colorStops: [
                    { offset: 0, color: '#1e3a5f' },
                    { offset: 0.5, color: '#1e3a5f' },
                    { offset: 0.5, color: '#4682B4' },
                    { offset: 1, color: '#4682B4' }
                  ]
                }
              }
            }
          ],
          bottom: 5,
          textStyle: { fontSize: 11 },
          itemWidth: 20,
          itemHeight: 14,
          formatter: (name) => {
            if (name === 'Madrugada') return 'Madrugada\n00-07'
            if (name === 'Diurno') return 'Diurno\n07-21'
            if (name === 'Nocturno') return 'Nocturno\n21-00'
            return ''
          }
        },
        grid: {
          left: '3%',
          right: '3%',
          bottom: '15%',
          top: '8%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: dateLabels,
          axisLabel: {
            fontSize: 10,
            interval: dateInterval,
            rotate: 45
          }
        },
        yAxis: {
          type: 'value',
          name: 'dBA',
          nameLocation: 'middle',
          nameGap: 35,
          nameRotate: 90,
          nameTextStyle: { fontSize: 11 },
          min: minY
        },
        series: [
          // Madrugada base
          {
            name: 'Madrugada',
            type: 'bar',
            stack: 'Madrugada',
            data: madrugadaBase,
            itemStyle: {
              color: '#ADD8E6',
              borderWidth: 1
            },
            barGap: '5%',
            barCategoryGap: '20%',
            label: {
              show: showLabels,
              position: 'inside',
              fontSize: 10,
              color: '#fff',
              fontWeight: 'bold'
            }
          },
          // Madrugada second
          {
            name: 'Madrugada-second',
            type: 'bar',
            stack: 'Madrugada',
            data: madrugadaSecond,
            itemStyle: {
              color: 'rgba(99, 142, 163, 0.85)',
              borderWidth: 1
            },
            label: {
              show: showLabels,
              position: 'inside',
              fontSize: 10,
              color: '#fff',
              fontWeight: 'bold',
              formatter: (params) => {
                if (!params.value) return ''
                return (madrugadaBase[params.dataIndex] + params.value) + ''
              }
            },
            legendHoverLink: false
          },
          // Diurno base
          {
            name: 'Diurno',
            type: 'bar',
            stack: 'Diurno',
            data: diurnoBase,
            itemStyle: {
              color: '#FFA500',
              borderWidth: 1
            },
            label: {
              show: showLabels,
              position: 'inside',
              fontSize: 10,
              color: '#fff',
              fontWeight: 'bold'
            }
          },
          // Diurno second
          {
            name: 'Diurno-second',
            type: 'bar',
            stack: 'Diurno',
            data: diurnoSecond,
            itemStyle: {
              color: 'rgba(204, 132, 0, 0.85)',
              borderWidth: 1
            },
            label: {
              show: showLabels,
              position: 'inside',
              fontSize: 10,
              color: '#fff',
              fontWeight: 'bold',
              formatter: (params) => {
                if (!params.value) return ''
                return (diurnoBase[params.dataIndex] + params.value) + ''
              }
            },
            legendHoverLink: false
          },
          // Nocturno base
          {
            name: 'Nocturno',
            type: 'bar',
            stack: 'Nocturno',
            data: nocturnoBase,
            itemStyle: {
              color: '#4682B4',
              borderWidth: 1
            },
            label: {
              show: showLabels,
              position: 'inside',
              fontSize: 10,
              color: '#fff',
              fontWeight: 'bold'
            }
          },
          // Nocturno second
          {
            name: 'Nocturno-second',
            type: 'bar',
            stack: 'Nocturno',
            data: nocturnoSecond,
            itemStyle: {
              color: '#1e3a5f',
              borderWidth: 1
            },
            label: {
              show: showLabels,
              position: 'inside',
              fontSize: 10,
              color: '#fff',
              fontWeight: 'bold',
              formatter: (params) => {
                if (!params.value) return ''
                const total = nocturnoBase[params.dataIndex] + params.value
                return total + ''
              }
            },
            legendHoverLink: false
          },
          {
            name: 'Madrugada-third',
            type: 'bar',
            stack: 'Madrugada',
            data: madrugadaThird,
            itemStyle: {
              color: '#0d1f2d',
              borderWidth: 1
            },
            label: {
              show: showLabels,
              position: 'inside',
              fontSize: 10,
              color: '#fff',
              fontWeight: 'bold',
              formatter: (params) => {
                if (!params.value) return ''
                return (madrugadaBase[params.dataIndex] + (madrugadaSecond[params.dataIndex] || 0) + params.value) + ''
              }
            },
            legendHoverLink: false
          },
          {
            name: 'Diurno-third',
            type: 'bar',
            stack: 'Diurno',
            data: diurnoThird,
            itemStyle: {
              color: '#663300',
              borderWidth: 1
            },
            label: {
              show: showLabels,
              position: 'inside',
              fontSize: 10,
              color: '#fff',
              fontWeight: 'bold',
              formatter: (params) => {
                if (!params.value) return ''
                return (diurnoBase[params.dataIndex] + (diurnoSecond[params.dataIndex] || 0) + params.value) + ''
              }
            },
            legendHoverLink: false
          },
          {
            name: 'Nocturno-third',
            type: 'bar',
            stack: 'Nocturno',
            data: nocturnoThird,
            itemStyle: {
              color: '#051018',
              borderWidth: 1
            },
            label: {
              show: showLabels,
              position: 'inside',
              fontSize: 10,
              color: '#fff',
              fontWeight: 'bold',
              formatter: (params) => {
                if (!params.value) return ''
                return (nocturnoBase[params.dataIndex] + (nocturnoSecond[params.dataIndex] || 0) + params.value) + ''
              }
            },
            legendHoverLink: false
          }
        ]
      }

      this.chartTrendsInstance.setOption(option)
    },

    processSummaryChart(dailyStatsRangeProcess) {
      if (!this.$refs.chartSummary) {
        return
      }

      // Inicializar o reutilizar instancia de ECharts
      if (!this.chartSummaryInstance) {
        this.chartSummaryInstance = echarts.init(this.$refs.chartSummary)
      }

      // Construir datos para 9 series (3 rangos x hasta 3 modas cada uno) - misma lógica que processTrendChart
      const madrugadaBase = []
      const madrugadaSecond = []
      const madrugadaThird = []
      const diurnoBase = []
      const diurnoSecond = []
      const diurnoThird = []
      const nocturnoBase = []
      const nocturnoSecond = []
      const nocturnoThird = []

      // Buscar cada grupo en dailyStatsRangeProcess y parsear su moda
      const madrugada = dailyStatsRangeProcess.find(g => g.grupo === 'Madrugada')
      const diurno = dailyStatsRangeProcess.find(g => g.grupo === 'Diurno')
      const nocturno = dailyStatsRangeProcess.find(g => g.grupo === 'Nocturno')

      const m = this.parseMode(madrugada && madrugada.moda).modes
      const d = this.parseMode(diurno && diurno.moda).modes
      const n = this.parseMode(nocturno && nocturno.moda).modes

      // Construir arrays de datos - cada posición representa una categoría en el eje X
      // [Madrugada, Diurno, Nocturno]
      madrugadaBase.push(m[0] != null ? m[0] : null)
      madrugadaBase.push(null)
      madrugadaBase.push(null)

      madrugadaSecond.push(m[1] != null && m[0] != null ? Math.abs(m[1] - m[0]) : null)
      madrugadaSecond.push(null)
      madrugadaSecond.push(null)

      madrugadaThird.push(m[2] != null && m[1] != null ? Math.abs(m[2] - m[1]) : null)
      madrugadaThird.push(null)
      madrugadaThird.push(null)

      diurnoBase.push(null)
      diurnoBase.push(d[0] != null ? d[0] : null)
      diurnoBase.push(null)

      diurnoSecond.push(null)
      diurnoSecond.push(d[1] != null && d[0] != null ? Math.abs(d[1] - d[0]) : null)
      diurnoSecond.push(null)

      diurnoThird.push(null)
      diurnoThird.push(d[2] != null && d[1] != null ? Math.abs(d[2] - d[1]) : null)
      diurnoThird.push(null)

      nocturnoBase.push(null)
      nocturnoBase.push(null)
      nocturnoBase.push(n[0] != null ? n[0] : null)

      nocturnoSecond.push(null)
      nocturnoSecond.push(null)
      nocturnoSecond.push(n[1] != null && n[0] != null ? Math.abs(n[1] - n[0]) : null)

      nocturnoThird.push(null)
      nocturnoThird.push(null)
      nocturnoThird.push(n[2] != null && n[1] != null ? Math.abs(n[2] - n[1]) : null)

      // Calcular rango Y dinámico - misma lógica que processTrendChart
      const allNumbers = [
        ...madrugadaBase, ...diurnoBase, ...nocturnoBase
      ].filter(v => v !== null)

      madrugadaBase.forEach((base, i) => {
        if (base !== null && madrugadaSecond[i] !== null) allNumbers.push(base + madrugadaSecond[i])
        if (base !== null && madrugadaSecond[i] !== null && madrugadaThird[i] !== null) {
          allNumbers.push(base + madrugadaSecond[i] + madrugadaThird[i])
        }
      })
      diurnoBase.forEach((base, i) => {
        if (base !== null && diurnoSecond[i] !== null) allNumbers.push(base + diurnoSecond[i])
        if (base !== null && diurnoSecond[i] !== null && diurnoThird[i] !== null) {
          allNumbers.push(base + diurnoSecond[i] + diurnoThird[i])
        }
      })
      nocturnoBase.forEach((base, i) => {
        if (base !== null && nocturnoSecond[i] !== null) allNumbers.push(base + nocturnoSecond[i])
        if (base !== null && nocturnoSecond[i] !== null && nocturnoThird[i] !== null) {
          allNumbers.push(base + nocturnoSecond[i] + nocturnoThird[i])
        }
      })

      const minY = allNumbers.length > 0 ? Math.min(...allNumbers) - 5 : 40

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            let result = ''

            params.forEach(p => {
              if (p.seriesName.includes('-second') || p.seriesName.includes('-third')) return
              if (p.value === null || p.value === undefined) return

              const baseVal = p.value
              const values = [baseVal]

              const secondSeries = params.find(s => s.seriesName === p.seriesName + '-second')
              if (secondSeries && secondSeries.value !== null && secondSeries.value !== undefined && secondSeries.value > 0) {
                values.push(baseVal + secondSeries.value)
              }

              const thirdSeries = params.find(s => s.seriesName === p.seriesName + '-third')
              if (thirdSeries && thirdSeries.value !== null && thirdSeries.value !== undefined && thirdSeries.value > 0) {
                values.push(baseVal + (secondSeries && secondSeries.value ? secondSeries.value : 0) + thirdSeries.value)
              }

              const line = p.seriesName + ': <strong>' + values.join(', ') + ' dBA</strong>'
              result += line + '<br/>'
            })
            return result
          }
        },
        legend: {
          data: [
            {
              name: 'Madrugada',
              icon: 'rect',
              itemStyle: {
                color: {
                  type: 'linear',
                  x: 0, y: 0, x2: 0, y2: 1,
                  colorStops: [
                    { offset: 0, color: 'rgba(99, 142, 163, 0.85)' },
                    { offset: 0.5, color: 'rgba(99, 142, 163, 0.85)' },
                    { offset: 0.5, color: '#ADD8E6' },
                    { offset: 1, color: '#ADD8E6' }
                  ]
                }
              }
            },
            {
              name: 'Diurno',
              icon: 'rect',
              itemStyle: {
                color: {
                  type: 'linear',
                  x: 0, y: 0, x2: 0, y2: 1,
                  colorStops: [
                    { offset: 0, color: 'rgba(204, 132, 0, 0.85)' },
                    { offset: 0.5, color: 'rgba(204, 132, 0, 0.85)' },
                    { offset: 0.5, color: '#FFA500' },
                    { offset: 1, color: '#FFA500' }
                  ]
                }
              }
            },
            {
              name: 'Nocturno',
              icon: 'rect',
              itemStyle: {
                color: {
                  type: 'linear',
                  x: 0, y: 0, x2: 0, y2: 1,
                  colorStops: [
                    { offset: 0, color: '#1e3a5f' },
                    { offset: 0.5, color: '#1e3a5f' },
                    { offset: 0.5, color: '#4682B4' },
                    { offset: 1, color: '#4682B4' }
                  ]
                }
              }
            }
          ],
          bottom: 5,
          textStyle: { fontSize: 11 },
          itemWidth: 20,
          itemHeight: 14,
          formatter: (name) => {
            if (name === 'Madrugada') return 'Madrugada\n00-07'
            if (name === 'Diurno') return 'Diurno\n07-21'
            if (name === 'Nocturno') return 'Nocturno\n21-00'
            return ''
          }
        },
        grid: {
          left: '15%',
          right: '5%',
          bottom: '18%',
          top: '8%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: ['Madrugada', 'Diurno', 'Nocturno'],
          axisLabel: { show: false },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          name: 'dBA',
          nameLocation: 'middle',
          nameGap: 35,
          nameRotate: 90,
          nameTextStyle: { fontSize: 11 },
          min: minY
        },
        series: [
          // Madrugada base
          {
            name: 'Madrugada',
            type: 'bar',
            stack: 'total',
            data: madrugadaBase,
            itemStyle: {
              color: '#ADD8E6',
              borderWidth: 1
            },
            barWidth: '60%',
            label: {
              show: true,
              position: 'inside',
              fontSize: 12,
              color: '#fff',
              fontWeight: 'bold'
            }
          },
          // Madrugada second
          {
            name: 'Madrugada-second',
            type: 'bar',
            stack: 'total',
            data: madrugadaSecond,
            itemStyle: {
              color: 'rgba(99, 142, 163, 0.85)',
              borderWidth: 1
            },
            label: {
              show: true,
              position: 'inside',
              fontSize: 12,
              color: '#fff',
              fontWeight: 'bold',
              formatter: (params) => {
                if (!params.value) return ''
                return (madrugadaBase[params.dataIndex] + params.value) + ''
              }
            },
            legendHoverLink: false
          },
          {
            name: 'Madrugada-third',
            type: 'bar',
            stack: 'total',
            data: madrugadaThird,
            itemStyle: {
              color: '#0d1f2d',
              borderWidth: 1
            },
            label: {
              show: true,
              position: 'inside',
              fontSize: 12,
              color: '#fff',
              fontWeight: 'bold',
              formatter: (params) => {
                if (!params.value) return ''
                return (madrugadaBase[params.dataIndex] + (madrugadaSecond[params.dataIndex] || 0) + params.value) + ''
              }
            },
            legendHoverLink: false
          },
          // Diurno base
          {
            name: 'Diurno',
            type: 'bar',
            stack: 'total',
            data: diurnoBase,
            itemStyle: {
              color: '#FFA500',
              borderWidth: 1
            },
            label: {
              show: true,
              position: 'inside',
              fontSize: 12,
              color: '#fff',
              fontWeight: 'bold'
            }
          },
          // Diurno second
          {
            name: 'Diurno-second',
            type: 'bar',
            stack: 'total',
            data: diurnoSecond,
            itemStyle: {
              color: 'rgba(204, 132, 0, 0.85)',
              borderWidth: 1
            },
            label: {
              show: true,
              position: 'inside',
              fontSize: 12,
              color: '#fff',
              fontWeight: 'bold',
              formatter: (params) => {
                if (!params.value) return ''
                return (diurnoBase[params.dataIndex] + params.value) + ''
              }
            },
            legendHoverLink: false
          },
          {
            name: 'Diurno-third',
            type: 'bar',
            stack: 'total',
            data: diurnoThird,
            itemStyle: {
              color: '#663300',
              borderWidth: 1
            },
            label: {
              show: true,
              position: 'inside',
              fontSize: 12,
              color: '#fff',
              fontWeight: 'bold',
              formatter: (params) => {
                if (!params.value) return ''
                return (diurnoBase[params.dataIndex] + (diurnoSecond[params.dataIndex] || 0) + params.value) + ''
              }
            },
            legendHoverLink: false
          },
          // Nocturno base
          {
            name: 'Nocturno',
            type: 'bar',
            stack: 'total',
            data: nocturnoBase,
            itemStyle: {
              color: '#4682B4',
              borderWidth: 1
            },
            label: {
              show: true,
              position: 'inside',
              fontSize: 12,
              color: '#fff',
              fontWeight: 'bold'
            }
          },
          // Nocturno second
          {
            name: 'Nocturno-second',
            type: 'bar',
            stack: 'total',
            data: nocturnoSecond,
            itemStyle: {
              color: '#1e3a5f',
              borderWidth: 1
            },
            label: {
              show: true,
              position: 'inside',
              fontSize: 12,
              color: '#fff',
              fontWeight: 'bold',
              formatter: (params) => {
                if (!params.value) return ''
                return (nocturnoBase[params.dataIndex] + params.value) + ''
              }
            },
            legendHoverLink: false
          },
          {
            name: 'Nocturno-third',
            type: 'bar',
            stack: 'total',
            data: nocturnoThird,
            itemStyle: {
              color: '#051018',
              borderWidth: 1
            },
            label: {
              show: true,
              position: 'inside',
              fontSize: 12,
              color: '#fff',
              fontWeight: 'bold',
              formatter: (params) => {
                if (!params.value) return ''
                return (nocturnoBase[params.dataIndex] + (nocturnoSecond[params.dataIndex] || 0) + params.value) + ''
              }
            },
            legendHoverLink: false
          }
        ]
      }

      this.chartSummaryInstance.setOption(option)
    },

    checkData () {
      if (!this.deviceStatsTrend || !this.deviceStatsTrend.message) {
        return true
      }

      const { dayStatsRange, dailyStatsRangeProcess } = this.deviceStatsTrend.message

      const noProcessData = !dailyStatsRangeProcess || dailyStatsRangeProcess.length === 0

      const noDayData = !dayStatsRange ||
        dayStatsRange.length === 0 ||
        dayStatsRange.every(day => !day.grupos || day.grupos.length === 0)

      return noProcessData && noDayData
    },

    sortRange (date) {
      if (date.length == 1)
          return { from: date[0] }

      if (date.length == 2) {
          const compareDates = (date1, date2) => {
              if (date1 < date2)
                  return -1
              else if (date1 > date2)
                  return 1
              else
                  return 0
          }

          date.sort(compareDates)

          return { from: date[0], to: date[1] }
      }

      return null
    },

    formatDate(dateString) {
      const parts = dateString.split('-')
      if (parts.length === 3) {
        const day = parts[0]
        const month = parts[1]
        return `${day}-${month}`
      }
      return dateString
    }
  }
}
</script>

<style scoped>
.v-card-title {
  padding-bottom: 8px;
}
</style>
